import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PageIndices, TabIndices } from '../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { AdaptiveTabs, AdaptiveTabItem, useAdaptiveTab } from '@components/common/AdaptiveTabs.tsx';
import { toastService } from '@services/toast.service.ts';
import {
  AppleDiagnosticsConfig,
  GoogleDiagnosticsConfig,
  LiveCheckResponse,
  PaymentDiagnosticsResponse,
  PaymentProvider,
  paymentDiagnosticsService,
  WayForPayDiagnosticsConfig,
} from '@services/payment-diagnostics.service.ts';

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('uk-UA', { timeZone: userTimeZone }) : '—';

const PROVIDER_TABS: AdaptiveTabItem[] = [
  { value: 'apple', label: 'Apple Pay', icon: <AppleIcon />, iDoc: TabIndices.DEV_PAYMENT_DIAGNOSTICS_APPLE },
  { value: 'google', label: 'Google Pay', icon: <AndroidIcon />, iDoc: TabIndices.DEV_PAYMENT_DIAGNOSTICS_GOOGLE },
  { value: 'wayforpay', label: 'WayForPay', icon: <CreditCardIcon />, iDoc: TabIndices.DEV_PAYMENT_DIAGNOSTICS_WAYFORPAY },
];

const StatusChip: React.FC<{ ok: boolean; label: string }> = ({ ok, label }) => (
  <Chip size="small" label={label} color={ok ? 'success' : 'error'} variant="outlined" />
);

// NEW (2026-08-03): кореневі сертифікати Apple живуть 20-30 років — ротація
// не потребує автоматизації (див. обговорення з користувачем), достатньо
// вчасно ПОМІТИТИ наближення терміну. 90 днів — довільний, але розумний
// запас, щоб встигнути звичайним деплоєм (не аварійним) замінити файл.
const CERT_EXPIRY_WARNING_DAYS = 90;

const RootCertificatesList: React.FC<{ certificates: { filename: string; expiresAt: string }[] }> = ({ certificates }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
    {certificates.map((cert) => {
      const expiresAtMs = Date.parse(cert.expiresAt);
      const daysLeft = Number.isNaN(expiresAtMs) ? null : Math.floor((expiresAtMs - Date.now()) / (24 * 60 * 60 * 1000));
      const isExpired = daysLeft !== null && daysLeft < 0;
      const isWarning = daysLeft !== null && daysLeft >= 0 && daysLeft <= CERT_EXPIRY_WARNING_DAYS;

      return (
        <Box key={cert.filename} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            variant="outlined"
            color={isExpired ? 'error' : isWarning ? 'warning' : 'success'}
            label={`${cert.filename}: діє до ${cert.expiresAt}${daysLeft !== null ? ` (${isExpired ? 'СПЛИВ' : `${daysLeft} дн.`})` : ''}`}
          />
        </Box>
      );
    })}
  </Box>
);

// Кожен провайдер сам знає форму своєї конфігурації (config — навмисно
// не уніфікований, дивись коментар у payment-diagnostics.service.ts) — цей
// компонент лише знає, ЯКІ поля показати для кожного, не більше.
const ConfigFields: React.FC<{ provider: PaymentProvider; config: PaymentDiagnosticsResponse['config'] }> = ({ provider, config }) => {
  if (provider === 'apple') {
    const c = config as AppleDiagnosticsConfig;
    return (
      <Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip size="small" label={`bundleId: ${c.bundleId}`} variant="outlined" />
          <StatusChip ok={c.hasKeyId} label={c.hasKeyId ? 'keyId ✓' : 'keyId відсутній'} />
          <StatusChip ok={c.hasIssuerId} label={c.hasIssuerId ? 'issuerId ✓' : 'issuerId відсутній'} />
          <StatusChip ok={c.hasSigningKey} label={c.hasSigningKey ? 'signingKey ✓' : 'signingKey відсутній'} />
          <StatusChip ok={c.hasAppAppleId} label={c.hasAppAppleId ? 'appAppleId ✓' : 'appAppleId відсутній (production вимкнено)'} />
          <StatusChip ok={c.rootCertificatesCount > 0} label={`root-сертифікати: ${c.rootCertificatesCount}`} />
          <StatusChip ok={c.sandboxVerifierReady} label="sandbox verifier" />
          <StatusChip ok={c.productionVerifierReady} label="production verifier" />
        </Box>
        {c.rootCertificates.length > 0 && <RootCertificatesList certificates={c.rootCertificates} />}
      </Box>
    );
  }

  if (provider === 'google') {
    const c = config as GoogleDiagnosticsConfig;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Chip size="small" label={`packageName: ${c.packageName}`} variant="outlined" />
        <StatusChip ok={c.androidPublisherConfigured} label={c.androidPublisherConfigured ? 'Android Publisher API ✓' : 'Android Publisher API не налаштовано'} />
        {c.serviceAccountEmail && <Chip size="small" label={`service account: ${c.serviceAccountEmail}`} variant="outlined" />}
        <StatusChip ok={c.rtdnConfigured} label={c.rtdnConfigured ? 'RTDN (webhook) ✓' : 'RTDN (webhook) не налаштовано'} />
      </Box>
    );
  }

  const c = config as WayForPayDiagnosticsConfig;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      <StatusChip ok={!!c.merchantAccount} label={c.merchantAccount ? `merchantAccount: ${c.merchantAccount}` : 'merchantAccount відсутній'} />
      <StatusChip ok={c.hasSecretKey} label={c.hasSecretKey ? 'secretKey ✓' : 'secretKey відсутній'} />
      <Chip size="small" label={`domain: ${c.domainName}`} variant="outlined" />
      <Chip size="small" label={`apiUrl: ${c.apiUrl}`} variant="outlined" />
      <Chip size="small" label={c.sandboxMode ? 'режим: sandbox (без реальних мережевих викликів)' : 'режим: production'} color={c.sandboxMode ? 'warning' : 'default'} variant="outlined" />
    </Box>
  );
};

const ProviderDiagnosticsTab: React.FC<{ provider: PaymentProvider; label: string }> = ({ provider, label }) => {
  const [data, setData] = useState<PaymentDiagnosticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [liveCheck, setLiveCheck] = useState<LiveCheckResponse | null>(null);
  const [liveCheckLoading, setLiveCheckLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLiveCheck(null);
    try {
      const res = await paymentDiagnosticsService.getDiagnostics(provider);
      setData(res);
    } catch {
      toastService.error(`Не вдалося завантажити діагностику ${label}.`);
    } finally {
      setLoading(false);
    }
  }, [provider, label]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runLiveCheck = async () => {
    setLiveCheckLoading(true);
    try {
      const res = await paymentDiagnosticsService.runLiveCheck(provider);
      setLiveCheck(res);
      if (!res.ok) toastService.warning(`${label}: живий тест з'єднання не пройшов.`);
    } catch {
      toastService.error(`Не вдалося виконати живий тест з'єднання (${label}).`);
    } finally {
      setLiveCheckLoading(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box>;
  if (!data) return null;

  const { config, recentActivity } = data;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>Конфігурація на сервері</Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={fetchData}>Оновити</Button>
      </Box>
      <ConfigFields provider={provider} config={config} />

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Живий тест з'єднання</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Реальний read-only виклик API провайдера (без побічних ефектів) — запускається вручну, не при кожному відкритті сторінки.
      </Typography>
      <Button
        variant="outlined"
        startIcon={liveCheckLoading ? <CircularProgress size={16} /> : <NetworkCheckIcon />}
        disabled={liveCheckLoading}
        onClick={runLiveCheck}
      >
        Перевірити з'єднання
      </Button>
      {liveCheck && (
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <StatusChip ok={liveCheck.ok} label={liveCheck.ok ? 'З\'єднання працює' : 'Помилка з\'єднання'} />
          <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{liveCheck.detail}</Typography>
          {liveCheck.productIds && liveCheck.productIds.length > 0 && (
            <Typography variant="caption" color="text.secondary">Товари: {liveCheck.productIds.join(', ')}</Typography>
          )}
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Недавня активність (за даними нашої БД)</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary">Остання успішна оплата</Typography>
          <Typography variant="body1">{formatDate(recentActivity.lastSuccessAt)}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary">Остання невдала спроба</Typography>
          <Typography variant="body1">{formatDate(recentActivity.lastFailureAt)}</Typography>
          {recentActivity.lastFailureReason && (
            <Typography variant="caption" color="error">{recentActivity.lastFailureReason}</Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary">За 24 години</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Chip size="small" label={`успіх: ${recentActivity.last24h.success}`} color="success" variant="outlined" />
            <Chip size="small" label={`провал: ${recentActivity.last24h.failure}`} color={recentActivity.last24h.failure > 0 ? 'error' : 'default'} variant="outlined" />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary">За 7 днів</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Chip size="small" label={`успіх: ${recentActivity.last7d.success}`} color="success" variant="outlined" />
            <Chip size="small" label={`провал: ${recentActivity.last7d.failure}`} color={recentActivity.last7d.failure > 0 ? 'error' : 'default'} variant="outlined" />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const DevPaymentDiagnosticsPageContent: React.FC = () => {
  const currentTab = useAdaptiveTab(PROVIDER_TABS, { searchParamKey: 'tab', defaultValue: 'apple', storageKey: 'tab:dev-payment-diagnostics' }) as PaymentProvider;
  const currentTabMeta = PROVIDER_TABS.find((t) => t.value === currentTab)!;

  return (
    <Box data-i-doc={PageIndices.DEV_PAYMENT_DIAGNOSTICS}>
      <PageHint iDoc={PageIndices.DEV_PAYMENT_DIAGNOSTICS} />

      <PageCard>
        <AdaptiveTabs tabs={PROVIDER_TABS} searchParamKey="tab" defaultValue="apple" storageKey="tab:dev-payment-diagnostics" />

        <Box sx={{ pt: 2 }} data-i-doc={currentTabMeta.iDoc}>
          <ProviderDiagnosticsTab provider={currentTab} label={currentTabMeta.label} />
        </Box>
      </PageCard>
    </Box>
  );
};

export const DevPaymentDiagnosticsPage: React.FC = () => (
  <DashboardLayout>
    <DevPaymentDiagnosticsPageContent />
  </DashboardLayout>
);
